/**
 * Citation Display Utilities
 *
 * Shared utility functions for citation display components. Used by
 * InlineCitation, UniversalCitationCard, and other citation UI components.
 */

/** Source type display labels for human-readable source type names */
export const SOURCE_TYPE_LABELS: Record<string, string> = {
  FILES_RAG: 'File',
  WEB: 'Web',
  INCIDENT: 'Incident',
  EXERCISE: 'Exercise',
  SITREP: 'SITREP',
  NEO: 'NEO Analysis',
  CUSTOM: 'Custom Data',
};

/**
 * Clean up display name by removing redundant prefixes
 *
 * Removes prefixes like "Exercise:" or "Custom Data:" that are redundant when
 * the source type badge is already showing the source type.
 *
 * @param name - The source name to clean
 * @param sourceType - Optional source type for context-aware cleaning
 * @returns Cleaned display name
 */
export function cleanDisplayName(name: string, sourceType?: string): string {
  if (!name) return 'Unknown';

  // Remove "Exercise:" prefix for EXERCISE sources
  if (sourceType === 'EXERCISE' && name.startsWith('Exercise:')) {
    return name.replace(/^Exercise:\s*/, '').trim() || 'Exercise';
  }

  // Remove "Custom Data:" prefix for CUSTOM sources
  if (sourceType === 'CUSTOM' && name.startsWith('Custom Data:')) {
    return name.replace(/^Custom Data:\s*/, '').trim() || 'Custom Data';
  }

  return name;
}

/**
 * Truncate a display name to a maximum length with ellipsis
 *
 * @param name - The name to truncate
 * @param maxLength - Maximum length (default: 60)
 * @returns Truncated name with ellipsis if needed
 */
export function truncateDisplayName(name: string, maxLength = 60): string {
  if (!name || name.length <= maxLength) return name;
  return `${name.substring(0, maxLength - 3)}...`;
}

/**
 * Get human-readable source type label
 *
 * @param sourceType - The source type constant (e.g., 'FILES_RAG')
 * @returns Human-readable label (e.g., 'File')
 */
export function getSourceTypeLabel(sourceType: string): string {
  return SOURCE_TYPE_LABELS[sourceType] || sourceType;
}

/**
 * Human-viewable page for a CUSTOM citation source (census profile, USGS event
 * page, …). Clicking the citation opens it in a new tab. Only http(s) URLs are
 * honored.
 */
export function getCustomSourceUrl(source: {
  metadata?: { url?: string };
}): string | null {
  const url = source.metadata?.url;
  return typeof url === 'string' && /^https?:\/\//i.test(url) ? url : null;
}
